import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, KeyRound, LogOut, Menu } from "lucide-react";
import ChangePinDialog from "@/components/ChangePinDialog";
import { MODULES } from "@/config/modules";

function pageTitle(pathname) {
  if (pathname === "/") return "Dashboard";
  const mod = MODULES.find((m) => m.path !== "/" && pathname.startsWith(m.path));
  return mod?.title || "S.D. Infratech ERP";
}

export default function Topbar({ onMenuClick }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [pinOpen, setPinOpen] = useState(false);
  const initials = (user?.name || "")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header className="h-16 border-b border-slate-200 bg-white flex items-center justify-between px-6 lg:px-8" data-testid="app-topbar">
      <div className="flex items-center gap-3">
        <button
          className="md:hidden -ml-2 p-2 rounded-md text-slate-600 hover:bg-slate-100 transition-colors"
          onClick={onMenuClick}
          data-testid="sidebar-menu-toggle"
          aria-label="Open navigation menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
          {pageTitle(location.pathname)}
        </h2>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-3 rounded-full pl-1 pr-3 py-1 hover:bg-slate-100 transition-colors" data-testid="profile-menu-trigger">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-amber-600 text-white text-xs font-bold">{initials}</AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-semibold text-slate-900 leading-tight">{user?.name}</p>
              <p className="text-[11px] text-slate-500 font-mono leading-tight">{user?.employee_id}</p>
            </div>
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <Badge variant="secondary">{user?.role}</Badge>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="change-pin-menu-item" onClick={() => setPinOpen(true)}>
            <KeyRound className="h-4 w-4 mr-2" /> Change PIN
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="logout-menu-item" onClick={logout} className="text-red-600 focus:text-red-600">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ChangePinDialog open={pinOpen} onOpenChange={setPinOpen} />
    </header>
  );
}
